/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';

import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CommonService } from '@ao/shared/services/common.service';
import { Chip } from 'primeng/chip';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [Breadcrumb, RouterModule, Chip],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private destroy$ = new Subject<void>();
  protected commonService = inject(CommonService);

  items: MenuItem[] = [];

  // Static home item with accessible metadata — pass this to <p-breadcrumb [home]="home">
  home: MenuItem = {
    icon: 'pi pi-home',
    label: 'Home',
    title: 'Home button link',
    routerLink: ['/dashboard'],
    size: 'small',
    styleClass: 'f-12',
    ...({ ariaLabel: 'Home button link' } as any),
  };

  ngOnInit() {
    // Initial breadcrumb build
    this.updateBreadcrumb();

    // Listen for navigation changes and rebuild breadcrumbs
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.buildBreadcrumb(this.activatedRoute.root)),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        // Normalize and ensure accessible metadata for generated items
        const computed = (items || []).map(i => ({
          ...i,
          title: (i as any).title ?? `${i.label} button link`,
          ...({
            ariaLabel: (i as any).ariaLabel ?? `${i.label} button link`,
          } as any),
        }));

        // Remove any generated breadcrumb that is the same as the configured home label (normalized)
        const cleaned = computed.filter(
          i =>
            this.normalizeLabel((i.label ?? i.title ?? '').toString()) !==
            this.normalizeLabel(
              (this.home.label ?? this.home.title ?? '').toString()
            )
        );

        // Set only the generated trail in items. The <p-breadcrumb> receives `home` separately.
        this.items = this.dedupeBreadcrumbs(cleaned);

        // Update page title based on last non-home breadcrumb
        const lastNonHome = cleaned.length
          ? cleaned[cleaned.length - 1].label
          : undefined;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateBreadcrumb() {
    const computed = this.buildBreadcrumb(this.activatedRoute.root).map(i => ({
      ...i,
      title: (i as any).title ?? `${i.label} button link`,
      ...({
        ariaLabel: (i as any).ariaLabel ?? `${i.label} button link`,
      } as any),
    }));

    // Filter out any generated 'Home' label (normalized) to avoid duplicating the configured home item
    const cleaned = computed.filter(
      i =>
        this.normalizeLabel((i.label ?? i.title ?? '').toString()) !==
        this.normalizeLabel(
          (this.home.label ?? this.home.title ?? '').toString()
        )
    );

    // Only keep the generated trail; home is passed separately to the component template.
    this.items = this.dedupeBreadcrumbs(cleaned);
  }

  private buildBreadcrumb(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: MenuItem[] = []
  ): MenuItem[] {
    try {
      const children: ActivatedRoute[] = route.children;

      if (children.length === 0) {
        return breadcrumbs;
      }

      for (const child of children) {
        const routeURL: string = child.snapshot.url
          .map(segment => segment.path)
          .join('/');
        const nextUrl = routeURL ? `${url}/${routeURL}` : url;

        const label = child.snapshot.data['breadcrumb'];

        if (label && typeof label === 'string') {
          // Only add breadcrumb if it's not already present (avoid duplicates)
          const existingItem = breadcrumbs.find(
            item =>
              this.normalizeLabel(
                (item.label ?? item.title ?? '').toString()
              ) === this.normalizeLabel(label)
          );
          if (!existingItem) {
            breadcrumbs.push({
              label,
              // Provide accessible metadata for generated items
              title: `${label} button link`,
              ...({ ariaLabel: `${label} button link` } as any),
              routerLink: nextUrl ? [nextUrl] : undefined,
              styleClass: 'f-12',
            });
          }
        } else if (routeURL) {
          /* empty */
        }

        // Recurse into child routes to build full breadcrumb trail
        this.buildBreadcrumb(child, nextUrl, breadcrumbs);
      }

      // Add selection details to the last breadcrumb node if not already appended
      //  if (breadcrumbs.length > 0 && this.commonService.selectionDetails) {
      //    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
      //    if (lastBreadcrumb.label && !lastBreadcrumb.label.includes(`(${this.commonService.selectionDetails})`)) {
      //      lastBreadcrumb.label = `${lastBreadcrumb.label} (${this.commonService.selectionDetails})`;
      //     lastBreadcrumb.title = `${lastBreadcrumb.label} button link`;
      //   }
      //  }
      return breadcrumbs;
    } catch (error) {
      return breadcrumbs;
    }
  }

  // Helper: normalize string for reliable comparison
  private normalizeLabel(input: string): string {
    return (input ?? '').toString().trim().toLowerCase();
  }

  // Helper: remove duplicates by normalized label while preserving order
  private dedupeBreadcrumbs(items: MenuItem[]): MenuItem[] {
    const seen = new Set<string>();
    const result: MenuItem[] = [];

    for (const it of items) {
      const raw = (it.label ?? it.title ?? '').toString();
      const key = this.normalizeLabel(raw);
      if (!key) {
        // Use fallback (stringified object) for items without label/title
        const fallback = JSON.stringify(it);
        if (!seen.has(fallback)) {
          seen.add(fallback);
          result.push(it);
        }
        continue;
      }
      if (!seen.has(key)) {
        seen.add(key);
        result.push(it);
      }
    }

    return result;
  }

  get shouldShowContent(): '' | undefined | boolean {
    if (this.items && this.items.length > 0) {
      const lastItem = this.items[this.items.length - 1];
      return (
        lastItem &&
        lastItem.label &&
        [
          'FS Caption View',
          'Focus Area',
          'Work Detail',
          'Task Detail',
        ].includes(lastItem.label)
      );
    }
    return false;
  }
}
